"use client";

import {
  ExpressCheckoutElement,
  ExpressCheckoutElementProps,
} from "@stripe/react-stripe-js";

import {
  FRESH_PROCESSING_PHRASES,
} from "apps/website/components/feature/Checkout/shared/constants";
import ProcessingPaymentModal from "apps/website/components/feature/Modal/ProcessingPaymentModal/ProcessingPaymentModal";

import { CreateBasketResult } from "./types";
import { useConfirmPayment } from "./useConfirmPayment";

/** Inner component rendered inside the `<Elements>` provider so that
 * `useConfirmPayment` (which calls `useStripe` / `useElements`) can resolve
 * the Stripe & Elements instances from context.
 *
 * `createBasket` is injected from the version-specific wrapper so this
 * component (and `useConfirmPayment`) stays agnostic of v1 vs v2. */
export function PayPalExpressCheckoutInner({
  createBasket,
  onSuccess,
  onError,
}: Readonly<{
  createBasket: () => Promise<CreateBasketResult>;
  onSuccess: () => void;
  onError: (message: string) => void;
}>) {
  const { handleConfirm, isProcessing } = useConfirmPayment({ createBasket, onSuccess, onError });
  const expressCheckoutOptions: ExpressCheckoutElementProps = {
    onConfirm: handleConfirm,
    options: {
      billingAddressRequired: false,
      // Restrict visible express buttons to PayPal only. Apple/Google Pay and
      // Link are explicitly disabled even though the parent `<Elements>` runs
      // in automatic mode and would otherwise enumerate them.
      paymentMethods: {
        applePay: "never",
        googlePay: "never",
        link: "never",
        paypal: "auto",
      },
    },
  };

  return (
    <>
      <ExpressCheckoutElement {...expressCheckoutOptions} />
      { isProcessing && (
        <ProcessingPaymentModal
          phrases={ FRESH_PROCESSING_PHRASES }
          processingComplete={ false }
        />
      ) }
    </>
  );
}
